// Whether a cardholder or a staff user has two-factor authentication on, and the reset for someone who lost their
// phone (W23): it turns two-factor off for that account, which signs in with the password alone until they turn it
// on again. The API decides whether this staff member may reach that person at all.
import { adminApi } from '../api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { confirmed } from './ui';

export const RESET_CONFIRM =
  'Turn two-factor authentication off for this account? They will sign in with their password alone until they turn it on again.';

export function TwoFactorReset({ kind, id }: { kind: 'cardholder' | 'user'; id: string }) {
  const client = useQueryClient();
  const key = ['two-factor-of', kind, id];
  const status = useQuery({
    queryKey: key,
    queryFn: () => adminApi.twoFactorOf(kind === 'user' ? 'staff' : 'cardholder', id),
    retry: false,
  });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  if (!status.data) return null;

  async function reset() {
    if (!confirmed(RESET_CONFIRM)) return;
    setBusy(true);
    try {
      const ok = await adminApi.resetTwoFactor(kind === 'user' ? 'staff' : 'cardholder', id);
      setMessage(ok ? 'Two-factor authentication was reset.' : "Couldn't reset two-factor authentication.");
      await client.invalidateQueries({ queryKey: key });
    } catch {
      setMessage("Couldn't reset two-factor authentication.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 16 }}>
      <span>
        Two-factor authentication: <strong>{status.data.enabled ? 'On' : 'Off'}</strong>
      </span>
      {status.data.enabled && (
        <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void reset()}>
          Reset two-factor
        </button>
      )}
      {message && <span role="status">{message}</span>}
    </div>
  );
}
